import { Menu } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Role } from '@/types/roles';
import { LanguageSwitcher } from './LanguageSwitcher';
import { RoleSwitcher } from './RoleSwitcher';
import { UserMenu } from './UserMenu';

/**
 * The only element present on every signed-in screen: the way into the nav on
 * mobile, the lockup, the switchers, and the way out of the session.
 *
 * Fixed rather than scrolling because the hamburger is the sole route to
 * navigation on a phone, and a nav control that scrolls away strands anyone who
 * has read to the bottom of a long list.
 */
export function TopBar({
  roles,
  view,
  onChangeView,
  onOpenDrawer,
}: {
  roles: Role[];
  view: Role;
  onChangeView: (next: Role) => void;
  onOpenDrawer: () => void;
}) {
  const { t } = useLanguage();

  return (
    <header className="fixed inset-x-0 top-0 z-[60] flex h-16 items-center gap-3 border-b border-border-subtle bg-white px-4 lg:px-6">
      <button
        type="button"
        onClick={onOpenDrawer}
        aria-label={t('navOpen')}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted hover:bg-surface lg:hidden"
      >
        <Menu className="h-6 w-6" aria-hidden="true" />
      </button>

      {/* The motto sits beside the mark rather than under it: the header is
          64px and a stacked lockup would either shrink the wordmark or crowd
          the rule. Muted grey so it reads as a subtitle and never competes
          with the mark. Hidden on the narrowest screens, where the space
          belongs to the controls. */}
      <div className="flex min-w-0 items-center gap-3">
        <Logo className="shrink-0" />
        <span className="hidden truncate border-s border-border-subtle ps-3 text-sm text-muted sm:inline">
          {t('tagline')}
        </span>
      </div>

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        <RoleSwitcher roles={roles} view={view} onChange={onChangeView} />
        <LanguageSwitcher />
        <UserMenu />
      </div>
    </header>
  );
}
